import { HttpErrorResponse } from '@angular/common/http';

export interface ExtractedError {
  readonly title: string;
  readonly messages: readonly string[];
}

/**
 * Extract user-friendly error messages from backend ApiErrorResponse.
 *
 * Backend shape:
 * {
 *   success: false,
 *   error: { code: string, message: string, details?: { validation?: string[], [key]: string[] } },
 *   timestamp: string
 * }
 */
export function extractError(err: unknown): ExtractedError {
  if (!(err instanceof HttpErrorResponse)) {
    // Handle plain error objects (e.g. { error: { message: '...' } } or { message: '...' })
    if (err != null && typeof err === 'object') {
      const obj = err as Record<string, unknown>;
      const nested = obj['error'] as Record<string, unknown> | undefined;
      const msg = (nested && typeof nested['message'] === 'string')
        ? nested['message']
        : (typeof obj['message'] === 'string' ? obj['message'] : null);
      if (msg) {
        return { title: msg, messages: [msg] };
      }
    }
    return { title: 'Error', messages: ['An unexpected error occurred. Please try again.'] };
  }

  const body = err.error;
  const errorObj = body?.error;

  if (!errorObj || typeof errorObj.message !== 'string') {
    return { title: 'Error', messages: [statusMessage(err.status)] };
  }

  const title = errorObj.message;
  const details = errorObj.details;

  // Collect all detail messages from every key in details
  if (details && typeof details === 'object') {
    const messages: string[] = [];
    for (const key of Object.keys(details)) {
      const val = details[key];
      if (Array.isArray(val)) {
        messages.push(...val.filter((v: unknown) => typeof v === 'string'));
      }
    }
    if (messages.length > 0) {
      return { title, messages };
    }
  }

  return { title, messages: [title] };
}

/** Shorthand: returns a single joined string (for inline error divs) */
export function extractErrorMessage(err: unknown): string {
  const { title, messages } = extractError(err);
  if (messages.length === 1 && messages[0] === title) return title;
  return `${title}: ${messages.join('; ')}`;
}

function statusMessage(status: number): string {
  switch (status) {
    case 400: return 'Invalid request. Please check your input.';
    case 401: return 'Session expired. Please log in again.';
    case 403: return 'You do not have permission for this action.';
    case 404: return 'The requested resource was not found.';
    case 409: return 'A conflict occurred. The resource may have been modified.';
    case 422: return 'Validation failed. Please check your input.';
    case 429: return 'Too many requests. Please wait a moment.';
    default: return 'An unexpected error occurred. Please try again.';
  }
}
